import { Link, Navigate, useLocation } from "react-router";
import { useForm } from "react-hook-form";
import { useSession } from "@/platform/auth";
import { useAuth } from "@/platform/auth";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { Lock, CircleAlert } from "lucide-react";

type LoginFormValues = { email: string; password: string };

const FALLBACK_POST_LOGIN_DESTINATION = "/";

function getPostLoginDestination(state: unknown): string {
  if (!state || typeof state !== "object" || Array.isArray(state)) {
    return FALLBACK_POST_LOGIN_DESTINATION;
  }

  const from = (state as { from?: unknown }).from;
  if (!from || typeof from !== "object" || Array.isArray(from)) {
    return FALLBACK_POST_LOGIN_DESTINATION;
  }

  const { pathname, search = "", hash = "" } = from as Record<string, unknown>;
  if (
    typeof pathname !== "string" ||
    typeof search !== "string" ||
    typeof hash !== "string"
  ) {
    return FALLBACK_POST_LOGIN_DESTINATION;
  }

  const destination = `${pathname}${search}${hash}`;

  try {
    const resolved = new URL(destination, window.location.origin);
    const isSameOriginLocation =
      resolved.origin === window.location.origin &&
      resolved.pathname === pathname &&
      resolved.search === search &&
      resolved.hash === hash;

    if (isSameOriginLocation) return destination;
  } catch {
    return FALLBACK_POST_LOGIN_DESTINATION;
  }

  return FALLBACK_POST_LOGIN_DESTINATION;
}

export function LoginPage() {
  const location = useLocation();
  const { isAuthenticated } = useSession();
  const { signInWithPassword } = useAuth();
  const guestEmail = import.meta.env.VITE_SUPABASE_GUEST_EMAIL;
  const guestPassword = import.meta.env.VITE_SUPABASE_GUEST_PASSWORD;
  const hasGuestCredentials = Boolean(guestEmail && guestPassword);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = useForm<LoginFormValues>({
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (values: LoginFormValues) => {
    clearErrors("root");
    const { error: err } = await signInWithPassword(
      values.email,
      values.password,
    );
    if (err) setError("root", { type: "server", message: err });
  };

  const onGuestLogin = () => {
    if (!hasGuestCredentials) return;
    void signInWithPassword(guestEmail, guestPassword);
  };

  if (isAuthenticated) {
    return <Navigate to={getPostLoginDestination(location.state)} replace />;
  }

  return (
    <>
      <title>Login - Researchly</title>
      <div className="flex min-h-[70vh] items-center justify-center p-4">
        <div className="w-full max-w-md rounded-xl border p-6">
          <h1 className="mb-1 text-2xl font-semibold">Welcome back</h1>
          <p className="mb-6 text-sm text-muted-foreground">
            Sign in with your email and password.
          </p>

          <form
            className="space-y-3"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <label className="text-sm font-medium" htmlFor="email">
              Email
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "email-error" : undefined}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value:
                    /^(?=.{1,254}$)(?=.{1,64}@)(?!\.)(?!.*\.\.)[A-Za-z0-9._%+-]+(?<!\.)@(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,63}$/i,
                  message: "Enter a valid email",
                },
              })}
              disabled={isSubmitting}
            />
            {errors.email && (
              <p
                id="email-error"
                className="text-xs text-destructive"
                aria-live="polite"
              >
                {errors.email.message}
              </p>
            )}
            <label className="text-sm font-medium" htmlFor="password">
              Password
            </label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? "password-error" : undefined}
              {...register("password", { required: "Password is required" })}
              disabled={isSubmitting}
            />
            {errors.password && (
              <p
                id="password-error"
                className="text-xs text-destructive"
                aria-live="polite"
              >
                {errors.password.message}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              <Lock className="mr-2 h-4 w-4" />
              Sign in
            </Button>
          </form>

          {errors.root && (
            <Alert variant="destructive" className="mt-3">
              <CircleAlert />
              <AlertTitle>Sign in failed</AlertTitle>
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <div className="mt-6 text-right text-sm">
            <Link
              to="/reset-password"
              className="text-muted-foreground underline"
            >
              Forgot password?
            </Link>
          </div>

          {hasGuestCredentials && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or try demo access
                  </span>
                </div>
              </div>

              <div className="rounded-lg border border-zinc-500/5 p-4">
                <p className="mb-3 text-xs text-muted-foreground">
                  Want to explore the platform first? Log in with guest access
                  to try out features without creating an account.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  disabled={isSubmitting}
                  onClick={onGuestLogin}
                >
                  Log in as guest
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
